import React, {
  useEffect,
  useRef,
} from "react";
import {
  useLocation,
  useNavigate,
} from "react-router-dom";
import {
  usePreferences,
} from "../context/PreferencesContext";

type PageBottomNavProps = {
  authenticated?: boolean;
};

const HISTORY_KEY =
  "avaaio_internal_page_history_v1";

const NAV_LABELS = {
  en: {
    back: "Back",
    settings: "Settings",
    dashboard: "Dashboard",
  },
  es: {
    back: "Atrás",
    settings: "Ajustes",
    dashboard: "Panel",
  },
  fr: {
    back: "Retour",
    settings: "Paramètres",
    dashboard: "Tableau",
  },
  de: {
    back: "Zurück",
    settings: "Einstellungen",
    dashboard: "Übersicht",
  },
  it: {
    back: "Indietro",
    settings: "Impostazioni",
    dashboard: "Dashboard",
  },
  pt: {
    back: "Voltar",
    settings: "Configurações",
    dashboard: "Painel",
  },
} as const;

const EXCLUDED_PATHS = new Set([
  "/",
  "/register",
  "/setup",
  "/setup-profile",
  "/profile-setup",
  "/verify-email",
  "/subscribe",
  "/forgot-password",
  "/reset-password",
  "/reactivate",
  "/privacy-policy",
  "/terms-and-conditions",
  "/ai-disclaimer",
  "/credits",
]);

function normalizePath(value: string): string {
  const trimmed = String(value || "/").trim();

  if (!trimmed || trimmed === "/") {
    return "/";
  }

  return trimmed.replace(/\/+$/, "") || "/";
}

function isExcludedPath(pathname: string): boolean {
  const normalized = normalizePath(pathname);

  if (EXCLUDED_PATHS.has(normalized)) {
    return true;
  }

  return normalized.startsWith("/login");
}

function readHistory(): string[] {
  try {
    const raw = sessionStorage.getItem(HISTORY_KEY);

    if (!raw) {
      return [];
    }

    const parsed = JSON.parse(raw);

    if (!Array.isArray(parsed)) {
      return [];
    }

    return parsed
      .filter(
        (value): value is string =>
          typeof value === "string",
      )
      .map(normalizePath)
      .filter(
        (value) => !isExcludedPath(value),
      )
      .slice(-40);
  } catch {
    return [];
  }
}

function writeHistory(history: string[]): void {
  try {
    sessionStorage.setItem(
      HISTORY_KEY,
      JSON.stringify(history.slice(-40)),
    );
  } catch {
    // Navigation remains available without session storage.
  }
}

export default function PageBottomNav({
  authenticated = false,
}: PageBottomNavProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { preferences } = usePreferences();

  const navigatingBackRef = useRef(false);

  const pathname = normalizePath(
    location.pathname,
  );

  const isSupportRoute =
    pathname === "/support" ||
    pathname.startsWith("/support/");

  const supportFromSettings =
    isSupportRoute &&
    (location.state as any)?.supportContext ===
      "settings";

  const visible =
    authenticated &&
    (!isSupportRoute || supportFromSettings) &&
    pathname !== "/dashboard" &&
    !isExcludedPath(pathname);

  const languageKey = String(
    preferences.language || "en",
  )
    .toLowerCase()
    .split("-")[0] as keyof typeof NAV_LABELS;

  const labels =
    NAV_LABELS[languageKey] ||
    NAV_LABELS.en;

  const isDashboard =
    pathname === "/dashboard";

  const isSettings =
    pathname === "/settings";

  useEffect(() => {
    if (!visible) {
      document.body.classList.remove(
        "avaaio-bottom-nav-visible",
      );

      document.body.classList.remove(
        "avaaio-bottom-nav-dashboard",
      );

      return;
    }

    document.body.classList.add(
      "avaaio-bottom-nav-visible",
    );

    document.body.classList.toggle(
      "avaaio-bottom-nav-dashboard",
      isDashboard,
    );

    return () => {
      document.body.classList.remove(
        "avaaio-bottom-nav-visible",
      );

      document.body.classList.remove(
        "avaaio-bottom-nav-dashboard",
      );
    };
  }, [visible, isDashboard]);

  useEffect(() => {
    if (!visible) {
      return;
    }

    const history = readHistory();

    if (navigatingBackRef.current) {
      navigatingBackRef.current = false;

      if (
        history[history.length - 1] !==
        pathname
      ) {
        history.push(pathname);
      }

      writeHistory(history);
      return;
    }

    if (
      history[history.length - 1] !==
      pathname
    ) {
      history.push(pathname);
    }

    writeHistory(history);
  }, [pathname, visible]);

  if (isSupportRoute && !supportFromSettings) {
    return (
      <>
        <nav
          data-page-bottom-nav="true"
          data-current-route={pathname}
          aria-label="AVAAIO support navigation"
          className="avaaio-page-bottom-nav avaaio-page-bottom-nav--support"
        >
          <button
            type="button"
            className="avaaio-page-bottom-nav__button avaaio-page-bottom-nav__button--support-back"
            onClick={() => navigate("/login")}
            aria-label="Back to Login"
          >
            <svg
              aria-hidden="true"
              className="avaaio-support-back-icon"
              viewBox="0 0 20 20"
              width="18"
              height="18"
              focusable="false"
            >
              <path
                d="M12.5 4.5 6.5 10l6 5.5"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>

            <span>Back to Login</span>
          </button>
        </nav>

        <style>{`
          .avaaio-page-bottom-nav--support {
            position: fixed !important;

            left: 50% !important;
            right: auto !important;

            bottom:
              max(
                12px,
                env(safe-area-inset-bottom, 0px)
              ) !important;

            transform: translateX(-50%) !important;

            z-index: 2147483647 !important;
            pointer-events: auto !important;

            display: grid !important;
            grid-template-columns: minmax(0, 1fr) !important;
            align-items: stretch !important;

            width:
              min(
                420px,
                calc(
                  100vw -
                  max(
                    24px,
                    env(safe-area-inset-left, 0px) +
                    env(safe-area-inset-right, 0px)
                  )
                )
              ) !important;

            box-sizing: border-box !important;
            padding: 7px !important;

            border:
              1px solid
              color-mix(
                in srgb,
                var(--ava-face-accent, #16cfe0) 42%,
                rgba(255,255,255,0.12)
              ) !important;

            border-radius: 18px !important;

            background:
              linear-gradient(
                180deg,
                rgba(8,13,23,0.94),
                rgba(2,5,10,0.97)
              ) !important;

            box-shadow:
              0 12px 34px rgba(0,0,0,0.52),
              0 0 24px
                color-mix(
                  in srgb,
                  var(--ava-face-accent, #16cfe0) 20%,
                  transparent
                ) !important;

            backdrop-filter: blur(18px) !important;
            -webkit-backdrop-filter: blur(18px) !important;
          }

          .avaaio-page-bottom-nav__button--support-back {
            appearance: none !important;

            width: 100% !important;
            min-width: 0 !important;
            min-height: 48px !important;

            display: flex !important;
            align-items: center !important;
            justify-content: center !important;

            gap: 8px !important;
            padding: 9px 12px !important;

            border:
              1px solid
              color-mix(
                in srgb,
                var(--ava-face-accent, #16cfe0) 48%,
                transparent
              ) !important;

            border-radius: 13px !important;

            background:
              linear-gradient(
                180deg,
                color-mix(
                  in srgb,
                  var(--ava-face-accent, #16cfe0) 16%,
                  rgba(12,19,31,0.94)
                ),
                rgba(2,6,12,0.94)
              ) !important;

            color: var(--ava-text, #e7fbfd) !important;

            cursor: pointer !important;

            font-family:
              Orbitron,
              system-ui,
              sans-serif !important;

            font-size: 12px !important;
            font-weight: 800 !important;
            letter-spacing: 0.075em !important;
            line-height: 1 !important;
            text-transform: uppercase !important;

            box-shadow:
              inset 0 0 12px
                color-mix(
                  in srgb,
                  var(--ava-face-accent, #16cfe0) 8%,
                  transparent
                ) !important;

            transition:
              transform 150ms ease,
              border-color 150ms ease,
              box-shadow 150ms ease,
              filter 150ms ease !important;
          }

          .avaaio-page-bottom-nav__button--support-back:hover,
          .avaaio-page-bottom-nav__button--support-back:focus-visible {
            border-color:
              var(--ava-face-accent, #16cfe0) !important;

            box-shadow:
              inset 0 0 16px
                color-mix(
                  in srgb,
                  var(--ava-face-accent, #16cfe0) 14%,
                  transparent
                ),
              0 0 28px
                color-mix(
                  in srgb,
                  var(--ava-face-accent, #16cfe0) 28%,
                  transparent
                ) !important;
          }

          .avaaio-support-back-icon {
            display: block !important;

            width: 18px !important;
            min-width: 18px !important;
            height: 18px !important;

            flex: 0 0 18px !important;

            margin: 0 !important;
            padding: 0 !important;

            color:
              var(
                --ava-face-accent,
                #16cfe0
              ) !important;

            overflow: visible !important;
            transform: none !important;
          }
        `}</style>
      </>
    );
  }

  if (!visible) {
    return null;
  }

  const goBack = () => {
    const history = readHistory();

    if (
      history[history.length - 1] ===
      pathname
    ) {
      history.pop();
    }

    let destination =
      history[history.length - 1] ||
      "/dashboard";

    if (
      destination === pathname ||
      isExcludedPath(destination)
    ) {
      destination = "/dashboard";
    }

    writeHistory(history);
    navigatingBackRef.current = true;
    navigate(destination);
  };

  const goSettings = () => {
    if (!isSettings) {
      navigate("/settings");
    }
  };

  const goDashboard = () => {
    if (!isDashboard) {
      navigate("/dashboard");
    }
  };

  return (
    <>
      <nav
        data-page-bottom-nav="true"
        data-current-route={pathname}
        aria-label="AVAAIO page navigation"
        className={[
          "avaaio-page-bottom-nav",
          isDashboard
            ? "avaaio-page-bottom-nav--dashboard"
            : "",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <button
          type="button"
          className="avaaio-page-bottom-nav__button"
          onClick={goBack}
          aria-label={labels.back}
        >
          <svg
            aria-hidden="true"
            className="avaaio-page-bottom-nav__icon avaaio-page-bottom-nav__back-chevron"
            viewBox="0 0 20 20"
            width="18"
            height="18"
            focusable="false"
            style={{
              display: "block",
              width: "18px",
              minWidth: "18px",
              height: "18px",
              flex: "0 0 18px",
              margin: 0,
              padding: 0,
              overflow: "visible",
            }}
          >
            <path
              d="M12.5 4.5 6.5 10l6 5.5"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>

          <span>{labels.back}</span>
        </button>

        <button
          type="button"
          className={[
            "avaaio-page-bottom-nav__button",
            isSettings
              ? "avaaio-page-bottom-nav__button--active"
              : "",
          ]
            .filter(Boolean)
            .join(" ")}
          onClick={goSettings}
          aria-current={
            isSettings ? "page" : undefined
          }
          disabled={isSettings}
        >
          <span
            aria-hidden="true"
            className="avaaio-page-bottom-nav__icon"
          >
            ⚙
          </span>

          <span>{labels.settings}</span>
        </button>

        <button
          type="button"
          className={[
            "avaaio-page-bottom-nav__button",
            isDashboard
              ? "avaaio-page-bottom-nav__button--active"
              : "",
          ]
            .filter(Boolean)
            .join(" ")}
          onClick={goDashboard}
          aria-current={
            isDashboard ? "page" : undefined
          }
          disabled={isDashboard}
        >
          <span
            aria-hidden="true"
            className="avaaio-page-bottom-nav__icon"
          >
            ◇
          </span>

          <span>{labels.dashboard}</span>
        </button>
      </nav>

      <style>{`
        body.avaaio-bottom-nav-visible {
          padding-bottom:
            calc(
              84px +
              env(safe-area-inset-bottom, 0px)
            ) !important;
        }

        .avaaio-page-bottom-nav {
          position: fixed;
          left: 50%;
          bottom:
            max(
              10px,
              env(safe-area-inset-bottom, 0px)
            );
          transform: translateX(-50%);
          z-index: 2147483647;
          pointer-events: auto !important;

          display: grid;
          grid-template-columns:
            repeat(3, minmax(0, 1fr));
          align-items: stretch;
          gap: 8px;

          width:
            min(
              720px,
              calc(
                100vw -
                max(
                  24px,
                  env(safe-area-inset-left, 0px) +
                  env(safe-area-inset-right, 0px)
                )
              )
            );

          box-sizing: border-box;
          padding: 7px;

          border:
            1px solid
            color-mix(
              in srgb,
              var(
                --ava-face-accent,
                #16cfe0
              )
              42%,
              rgba(255, 255, 255, 0.12)
            );

          border-radius: 18px;

          background:
            linear-gradient(
              180deg,
              rgba(8, 13, 23, 0.94),
              rgba(2, 5, 10, 0.97)
            );

          box-shadow:
            0 12px 34px rgba(0, 0, 0, 0.52),
            0 0 24px
              color-mix(
                in srgb,
                var(
                  --ava-face-accent,
                  #16cfe0
                )
                20%,
                transparent
              );

          backdrop-filter: blur(18px);
          -webkit-backdrop-filter: blur(18px);
        }

        .avaaio-page-bottom-nav--dashboard {
          bottom:
            calc(
              88px +
              env(safe-area-inset-bottom, 0px)
            );
        }

        .avaaio-page-bottom-nav__button {
          appearance: none;
          pointer-events: auto !important;
          min-width: 0;
          min-height: 48px;

          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;

          padding: 9px 12px;

          border:
            1px solid
            color-mix(
              in srgb,
              var(
                --ava-face-accent,
                #16cfe0
              )
              48%,
              transparent
            );

          border-radius: 13px;

          background:
            linear-gradient(
              180deg,
              color-mix(
                in srgb,
                var(
                  --ava-face-accent,
                  #16cfe0
                )
                16%,
                rgba(12, 19, 31, 0.94)
              ),
              rgba(2, 6, 12, 0.94)
            );

          color:
            var(
              --ava-text,
              #e7fbfd
            );

          cursor: pointer;

          font-family:
            Orbitron,
            system-ui,
            sans-serif;

          font-size: 12px;
          font-weight: 800;
          letter-spacing: 0.075em;
          line-height: 1;
          text-transform: uppercase;

          box-shadow:
            inset 0 0 12px
              color-mix(
                in srgb,
                var(
                  --ava-face-accent,
                  #16cfe0
                )
                8%,
                transparent
              );

          transition:
            transform 150ms ease,
            border-color 150ms ease,
            box-shadow 150ms ease,
            filter 150ms ease;
        }

        .avaaio-page-bottom-nav__button:hover:not(:disabled),
        .avaaio-page-bottom-nav__button:focus-visible:not(:disabled) {
          transform: translateY(-2px);

          border-color:
            var(
              --ava-face-accent,
              #16cfe0
            );

          box-shadow:
            0 0 20px
              color-mix(
                in srgb,
                var(
                  --ava-face-accent,
                  #16cfe0
                )
                30%,
                transparent
              ),
            inset 0 0 14px
              color-mix(
                in srgb,
                var(
                  --ava-face-accent,
                  #16cfe0
                )
                12%,
                transparent
              );

          outline: none;
          filter: brightness(1.12);
        }

        .avaaio-page-bottom-nav__button:active:not(:disabled) {
          transform: translateY(1px);
        }

        .avaaio-page-bottom-nav__button--active,
        .avaaio-page-bottom-nav__button:disabled {
          border-color:
            var(
              --ava-face-accent,
              #16cfe0
            );

          background:
            color-mix(
              in srgb,
              var(
                --ava-face-accent,
                #16cfe0
              )
              17%,
              rgba(5, 10, 18, 0.96)
            );

          color:
            var(
              --ava-face-accent,
              #16cfe0
            );

          cursor: default;
          opacity: 0.82;

          box-shadow:
            0 0 16px
              color-mix(
                in srgb,
                var(
                  --ava-face-accent,
                  #16cfe0
                )
                20%,
                transparent
              );
        }

        .avaaio-page-bottom-nav__icon {
          flex: 0 0 auto;
          color:
            var(
              --ava-face-accent,
              #16cfe0
            );
          font-size: 18px;
          line-height: 1;
        }

        @media (max-width: 720px) {
          body.avaaio-bottom-nav-visible {
            padding-bottom:
              calc(
                72px +
                env(
                  safe-area-inset-bottom,
                  0px
                )
              ) !important;
          }

          .avaaio-page-bottom-nav {
            width:
              calc(
                100vw -
                max(
                  12px,
                  env(
                    safe-area-inset-left,
                    0px
                  ) +
                  env(
                    safe-area-inset-right,
                    0px
                  )
                )
              );

            gap: 5px;
            padding: 5px;
            border-radius: 15px;
          }

          .avaaio-page-bottom-nav--dashboard {
            bottom:
              calc(
                72px +
                env(
                  safe-area-inset-bottom,
                  0px
                )
              );
          }

          /*
           * AVAAIO GLOBAL BACK CENTERING
           * Scope only to the first/global Back button.
           * Settings and Dashboard remain untouched.
           */
          .avaaio-page-bottom-nav
          > .avaaio-page-bottom-nav__button:first-child {
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
            text-align: center !important;
            gap: 7px !important;
          }

          .avaaio-page-bottom-nav
          > .avaaio-page-bottom-nav__button:first-child
          > .avaaio-page-bottom-nav__icon {
            display: inline-flex !important;
            align-items: center !important;
            justify-content: center !important;
            width: auto !important;
            min-width: 0 !important;
            margin: 0 !important;
            padding: 0 !important;
            line-height: 1 !important;
            transform: none !important;
          }

          .avaaio-page-bottom-nav
          > .avaaio-page-bottom-nav__button:first-child
          > span:last-child {
            display: inline-flex !important;
            align-items: center !important;
            justify-content: center !important;
            margin: 0 !important;
            padding: 0 !important;
            line-height: 1 !important;
          }

          .avaaio-page-bottom-nav__button {
            min-height: 44px;
            gap: 5px;
            padding: 7px 5px;
            border-radius: 11px;
            font-size: 10px;
            letter-spacing: 0.04em;
          }

          .avaaio-page-bottom-nav__icon {
            font-size: 15px;
          }
        }

        @media (
          max-width: 932px
        ) and (
          max-height: 520px
        ) and (
          orientation: landscape
        ) {
          body.avaaio-bottom-nav-visible {
            padding-bottom:
              calc(
                52px +
                env(
                  safe-area-inset-bottom,
                  0px
                )
              ) !important;
          }

          .avaaio-page-bottom-nav {
            width:
              min(
                620px,
                calc(100vw - 12px)
              );

            bottom:
              max(
                3px,
                env(
                  safe-area-inset-bottom,
                  0px
                )
              );

            padding: 4px;
          }

          .avaaio-page-bottom-nav--dashboard {
            bottom:
              calc(
                56px +
                env(
                  safe-area-inset-bottom,
                  0px
                )
              );
          }

          .avaaio-page-bottom-nav__button {
            min-height: 36px;
            padding: 5px 4px;
            font-size: 9px;
          }

          .avaaio-page-bottom-nav__icon {
            font-size: 13px;
          }
        }
      `}</style>
    </>
  );
}
